/**
 * Pure session rules for D&D 5e combat, damage, healing, and rests.
 *
 * These functions encode game rules without any store/state management
 * dependencies. The store calls these and applies the results.
 */

/**
 * Apply damage to a character, with temp HP absorbing first.
 */
export function applyDamage(
  currentHp: number,
  tempHp: number,
  damage: number,
): { currentHp: number; tempHp: number } {
  let remainingDamage = damage;

  // Temp HP absorbs damage first
  let newTempHp = tempHp;
  if (newTempHp > 0) {
    const absorbed = Math.min(newTempHp, remainingDamage);
    newTempHp -= absorbed;
    remainingDamage -= absorbed;
  }

  return {
    currentHp: Math.max(0, currentHp - remainingDamage),
    tempHp: newTempHp,
  };
}

/**
 * Apply healing to a character, capped at max HP.
 * Returns whether death saves should be reset (healed from 0).
 */
export function applyHealing(
  currentHp: number,
  maxHp: number,
  amount: number,
): { currentHp: number; shouldResetDeathSaves: boolean } {
  const newHp = Math.min(maxHp, currentHp + amount);
  return {
    currentHp: newHp,
    shouldResetDeathSaves: newHp > 0,
  };
}

/**
 * Apply temporary HP (non-stacking: take higher value per 5e rules).
 */
export function applyTempHp(currentTempHp: number, newTempHp: number): number {
  return Math.max(currentTempHp, newTempHp);
}

/**
 * Calculate how many hit dice to restore on a long rest.
 * Per 5e rules: restore half of max (minimum 1) for each die type,
 * distributed to larger dice first.
 */
export function calculateHitDiceToRestore(
  hitDice: Record<string, { max: number; used: number }>,
): Record<string, number> {
  const restorations: Record<string, number> = {};

  for (const [dieType, die] of Object.entries(hitDice)) {
    const toRestore = Math.max(1, Math.floor(die.max / 2));
    restorations[dieType] = Math.min(toRestore, die.used);
  }

  return restorations;
}

/**
 * Calculate the effects of a short rest.
 * - Warlock pact slots recover on short rest.
 * - Hit dice spending happens manually (not part of this function).
 */
export function calculateShortRestEffects(
  spellSlots: Record<string, { max: number; used: number }>,
): { restorePactSlots: boolean } {
  return {
    restorePactSlots: spellSlots['pact'] !== undefined && spellSlots['pact'].used > 0,
  };
}

export interface LongRestEffects {
  restoreFullHp: boolean;
  clearTempHp: boolean;
  resetDeathSaves: boolean;
  restoreAllSpellSlots: boolean;
  hitDiceRestorations: Record<string, number>;
  restoreAllResources: boolean;
  reduceExhaustion: boolean;
}

/**
 * Calculate the effects of a long rest.
 * Per 5e rules:
 * - Full HP restored
 * - Temp HP cleared
 * - Death saves reset
 * - All spell slots restored
 * - Half hit dice restored (min 1 per type)
 * - All class resources restored
 * - Exhaustion reduced by 1
 */
export function calculateLongRestEffects(
  hitDice: Record<string, { max: number; used: number }>,
  exhaustionLevel: number,
): LongRestEffects {
  return {
    restoreFullHp: true,
    clearTempHp: true,
    resetDeathSaves: true,
    restoreAllSpellSlots: true,
    hitDiceRestorations: calculateHitDiceToRestore(hitDice),
    restoreAllResources: true,
    reduceExhaustion: exhaustionLevel > 0,
  };
}
